import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState, type CSSProperties, useImperativeHandle, ForwardedRef } from 'react';

import { Alert } from '../alert';
import { MenuItem, type MenuItemProps } from '../menu-item';

import { useElementResize } from '../../hooks/use-element-resize';

import { hasFocusedInput, toRem } from '../../utils/ui';
import { conditionalSpread } from '../../utils/helper-utils';

import styles from './menu.module.scss';

export const Menu = (props: MenuProps) => {
  const { data, onExitSubMenu, className, forwardedRef, closePopover, initialActiveCustomView, ...rest } = props;

  const ref = useRef<HTMLDivElement | null>(null);

  const [transitionEnded, setTransitionEnded] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState<string>();
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null);
  const [activeCustomView, setActiveCustomView] = useState<MenuItemProps | null>(initialActiveCustomView || null);
  const [sizeToContent, setSizeToContent] = useState(false);

  const { ref: containerRef, clientHeight } = useElementResize<HTMLLIElement>();

  const hasSelection = useMemo(() => data.some((el) => el.selected), [data]);

  const onShowConfirmation = (itemName: string) => {
    const deleteConfirmation = data.find((item) => item.showConfirmation && itemName === item.name);
    !!deleteConfirmation && setShowConfirmation(deleteConfirmation.name);
  };

  const shouldSkip = (el: MenuItemProps) => {
    const types = ['divider', 'group'];
    return (el.type && types.includes(el.type)) || el.disabled;
  };

  const onItemClick = async (item: MenuItemProps) => {
    setTransitionEnded(false);
    if (item.type === 'custom-view') {
      setSizeToContent(false);
      setActiveCustomView(item);
    }

    if (item?.showConfirmation) {
      item.showConfirmation && onShowConfirmation(item.name!);
    } else {
      try {
        await item?.onClick?.();
      } catch {
        // Suppress errors from menu item handlers (e.g., clipboard failures)
      } finally {
        closePopover?.();
      }
    }
  };

  useEffect(() => {
    if (activeCustomView) {
      setActiveCustomView(data.find((el) => el.type === 'custom-view' && activeCustomView.name === el.name)!);
    }
  }, [data]);

  const handleNavigation = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      setHighlightIndex((val) => {
        const nextIndex = val === null ? 0 : val + 1;
        return data.findIndex((el, idx) => !shouldSkip(el) && idx >= (nextIndex > data.length - 1 ? 0 : nextIndex));
      });
    }
    if (e.key === 'ArrowUp') {
      setHighlightIndex((val) => {
        const nextIndex = val === null ? 0 : val - 1;
        return data.findLastIndex((el, idx) => !shouldSkip(el) && idx <= (nextIndex <= 0 ? data.length - 1 : nextIndex));
      });
    }
    if (e.key === 'Enter' && highlightIndex !== null) {
      onItemClick(data.find((_, index) => index === highlightIndex)!);
    }
  };

  useImperativeHandle(forwardedRef, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
        handleNavigation(event);
        return true;
      }
      return false;
    },
  }));

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
        if (isInteractiveKeyTarget(e.target)) return;

        e.stopPropagation();
        e.preventDefault();
      }

      if (e.key === 'Escape') {
        closePopover?.();
      }

      handleNavigation(e);

      if (e.metaKey && e.key === 'Backspace' && !hasFocusedInput()) {
        // onShowConfirmation();
        e.stopPropagation();
        e.preventDefault();
      }
    };

    if (ref.current && !forwardedRef) {
      ref.current.focus();
      ref.current.addEventListener('keydown', onKeyDown);
    }

    return () => {
      ref.current?.removeEventListener('keydown', onKeyDown);
    };
  }, [ref, highlightIndex]);

  const handleGoBack = () => {
    setSizeToContent(false);
    setActiveCustomView(null);
    setTransitionEnded(false);
    requestAnimationFrame(() => onExitSubMenu?.());
  };

  // Release once settled in a custom view (transitionend may not fire if height is unchanged).
  useEffect(() => {
    if (!activeCustomView || sizeToContent) return;
    const timeoutId = setTimeout(() => setSizeToContent(true), 350);
    return () => clearTimeout(timeoutId);
  }, [activeCustomView, sizeToContent, clientHeight]);

  const renderMenuItem = (item: MenuItemProps, index: number) => {
    return (
      <MenuItem
        key={index}
        highlighted={highlightIndex === index}
        hasSelection={hasSelection}
        {...item}
        closeMenu={closePopover}
        onClick={() => onItemClick(item)}
      />
    );
  };

  const hasCustomView = useMemo(() => data.some((el) => el.type === 'custom-view'), []);
  const lockHeight = !!clientHeight && hasCustomView && !sizeToContent;

  return (
    <div
      ref={ref}
      tabIndex={0}
      className={clsx(styles.menu, className)}
      style={conditionalSpread({ height: toRem(clientHeight + 12 + 2) }, lockHeight)}
      onTransitionEnd={(e) => {
        if (e.target === e.currentTarget && e.propertyName === 'height' && activeCustomView) {
          setSizeToContent(true);
        }
      }}
      {...rest}
    >
      <ul
        style={{ '--x': `-${(!activeCustomView ? 0 : 1) * 100}%` } as CSSProperties}
        onTransitionEnd={() => setTransitionEnded(true)}
      >
        {!!activeCustomView && <li />}
        <li ref={containerRef}>
          {!!activeCustomView ? (
            <>
              <MenuItem
                {...activeCustomView}
                type="header"
                name={activeCustomView.customViewTitle ?? activeCustomView.name}
                icon="arrow-left-01"
                onClick={handleGoBack}
              />
              <MenuItem type="divider" />
              {activeCustomView.renderer?.({ transitionEnded })}
            </>
          ) : (
            data.map((item, index) => {
              return item.showConfirmation ? (
                <Alert
                  key={index}
                  type={item.deleteData?.type ?? 'danger'}
                  color={item.deleteData?.color}
                  icon={item.deleteData?.icon}
                  withOverlay
                  open={showConfirmation === item.name}
                  message={item.deleteData?.message as string}
                  title={item.deleteData?.title}
                  trigger={renderMenuItem(item, index)}
                  onClose={() => {
                    setShowConfirmation(undefined);
                    ref.current?.focus();
                  }}
                  primaryButton={item.deleteData?.primaryButton}
                  secondaryButton={item.deleteData?.secondaryButton}
                  popoverProps={{
                    offset: [0, 16],
                    placement: 'right',
                  }}
                />
              ) : (
                renderMenuItem(item, index)
              );
            })
          )}
        </li>
      </ul>
    </div>
  );
};

export type MenuData = MenuItemProps[];

export interface MenuProps {
  data: MenuData;
  className?: string;
  closePopover?: () => void;
  onExitSubMenu?: () => void;
  initialActiveCustomView?: MenuItemProps;
  forwardedRef?: ForwardedRef<{
    onKeyDown?: ({ event }: { event: KeyboardEvent }) => boolean;
  }>;
}

const isInteractiveKeyTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;

  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || target.isContentEditable;
};
